import { createRef, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import ConfirmationModal from "./ConfirmationModal";
import NewQuestionForm from "./NewQuestionForm";

function NewQuestionDialog({
  open,
  onClose,
  onSave,
  courseData,
  unitId,
  level,
  startingOrder = 0,
}) {
  const [formRows, setFormRows] = useState([]);
  const [formData, setFormData] = useState({});
  const [statusMessage, setStatusMessage] = useState(null);
  const formCounterRef = useRef(0);
  const formRefs = useRef({});

  const levelId = level?._id || null;
  const levelName = level?.name || "this level";
  const levelOrder = level?.order;

  const computedBaseOrder = useMemo(() => {
    if (startingOrder) {
      return startingOrder;
    }
    
    if (!level?.questions?.length) {
      return 0;
    }

    const orders = level.questions
      .map((question) => Number(question.order))
      .filter((order) => Number.isFinite(order));
    return orders.length ? Math.max(...orders) : 0;
  }, [level, startingOrder]);

  const baseOrder = computedBaseOrder;

  const createFormId = () => {
    formCounterRef.current += 1;
    return `bulk-question-${Date.now()}-${formCounterRef.current}`;
  };

  useEffect(() => {
    if (!open) {
      return;
    }

    formCounterRef.current = 0;
    formRefs.current = {};
    const firstId = createFormId();

    setFormRows([{ id: firstId }]);
    setFormData({});
    setStatusMessage(null);
  }, [open, levelId, baseOrder]);

  const getFormRef = (formId) => {
    if (!formRefs.current[formId]) {
      formRefs.current[formId] = createRef();
    }
    return formRefs.current[formId];
  };

  const handleDialogClose = (event, reason) => {
    if (reason === "backdropClick") {
      return;
    }
    onClose?.();
  };

  const handleFormChange = (formId, data) => {
    setFormData((prev) => {
      if (!data?.payload?.question?.trim()) {
        if (!prev[formId]) {
          return prev;
        }

        const next = { ...prev };
        delete next[formId];
        return next;
      }
      return { ...prev, [formId]: data };
    });
  };

  const handleAddForm = () => {
    setFormRows((prev) => [...prev, { id: createFormId() }]);
    setStatusMessage(null);
  };

  const handleRemoveForm = (formId) => {
    setFormRows((prev) => {
      const next = prev.filter((row) => row.id !== formId);
      if (next.length) {
        return next;
      }
      return [{ id: createFormId() }];
    });
    delete formRefs.current[formId];

    setFormData((prev) => {
      if (!prev[formId]) {
        return prev;
      }
      const next = { ...prev };
      delete next[formId];
      return next;
    });

    setStatusMessage(null);
  };

  const questionEntries = useMemo(() => {
    return formRows.reduce((accumulator, row) => {
      const entry = formData[row.id];

      if (!entry?.payload?.question?.trim()) {
        return accumulator;
      }

      const order = baseOrder + accumulator.length + 1;
      accumulator.push({
        payload: { ...entry.payload, order },
        attachments: entry.attachments,
      });

      return accumulator;
    }, []);
  }, [formRows, formData, baseOrder]);

  // Generate summary items for confirmation modal
  const questionSummaryItems = useMemo(() => {
    return questionEntries.map((entry, index) => {
      const trimmedText = entry.payload.question?.trim() || "Untitled question";
      const readableText =
        trimmedText.length > 80 ? `${trimmedText.slice(0, 77)}...` : trimmedText;
      const orderLabel = entry.payload.order ?? baseOrder + index + 1;
      return {
        id: `${entry.payload.question || "question"}-${index}`,
        label: `Q${orderLabel}`,
        description: readableText,
      };
    });
  }, [questionEntries, baseOrder]);

  const hasUnsavedChanges = useMemo(() => Object.keys(formData).length > 0, [formData]);

  const handleSaveAll = () => {
    if (!levelId) {
      setStatusMessage({
        type: "error",
        text: "Level data is missing. Close the modal and try again.",
      });
      return;
    }

    if (!questionEntries.length) {
      setStatusMessage({
        type: "error",
        text: "Add at least one valid question before saving.",
      });
      return;
    }

    const levelLabel = levelOrder ? `Level ${levelOrder}` : levelName;
    const normalizedEntries = [];
    const validationErrors = [];

    formRows.forEach((row, rowIndex) => {
      const entry = formData[row.id];
      const questionNumber = baseOrder + rowIndex + 1;

      // Require question name
      if (!entry?.payload?.question?.trim()) {
        validationErrors.push(
          `Fill or remove ${levelLabel}, Question ${questionNumber}.`,
        );
        return;
      }

      const formRef = formRefs.current[row.id];
      const validatedData = formRef?.current?.validateAndBuild?.();
      if (!validatedData) {
        validationErrors.push(
          `Fix validation issues in ${levelLabel}, Question ${questionNumber}.`,
        );
        return;
      }

      normalizedEntries.push({
        payload: {
          ...validatedData.payload,
          order: baseOrder + normalizedEntries.length + 1,
          parentCourseId: courseData.courseId,
          parentUnitId: unitId,
          parentLevelId: levelId,
        },
        attachments: validatedData.attachments,
      });
    });

    if (validationErrors.length) {
      setStatusMessage({
        type: "error",
        text: validationErrors[0],
      });
      return;
    }

    if (!normalizedEntries.length) {
      setStatusMessage({
        type: "error",
        text: "No valid question data found.",
      });
      return;
    }

    onSave?.(normalizedEntries);
    setStatusMessage(null);
    onClose?.();
  };

  const readyCount = questionEntries.length;
  const isSaveDisabled = !readyCount || !levelId;

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={open}
      scroll="paper"
      onClose={handleDialogClose}
      disableEscapeKeyDown
      sx={{ borderRadius: 0 }}
    >
      <DialogTitle>
        Add Questions to {levelOrder ? `Level ${levelOrder}` : levelName}
      </DialogTitle>
      <DialogContent dividers={true}>
        <Stack spacing={2}>
          {statusMessage && (
            <Alert severity={statusMessage.type}>{statusMessage.text}</Alert>
          )}

          <Box
            sx={{
              maxHeight: "60vh",
              overflowY: "auto",
              p: 3,
            }}
          >
            {formRows.map((row, index) => (
              <Box key={row.id} sx={{ mb: 3 }}>
                <NewQuestionForm
                  ref={getFormRef(row.id)}
                  courseData={courseData}
                  unitId={unitId}
                  levelId={levelId || ""}
                  levelOrder={levelOrder}
                  nextOrder={baseOrder + index + 1}
                  displayConfirmButton={false}
                  secondaryBtnText="Remove Question"
                  onCancel={() => handleRemoveForm(row.id)}
                  onChange={(data) => handleFormChange(row.id, data)}
                />
                {index < formRows.length - 1 && <Divider sx={{ mt: 3 }} />}
              </Box>
            ))}
          </Box>

          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "end" }}>
              Ready to save: {readyCount} question{readyCount === 1 ? "" : "s"}
            </Typography>

            <Button variant="outlined" color="info" onClick={handleAddForm}>
              Add Another Question
            </Button>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <ConfirmationModal
          title="Save Questions?"
          description={`You're about to create ${readyCount} question${readyCount === 1 ? "" : "s"}.`}
          helperText={
            courseData?.isDraft || courseData?.isDeleted
              ? ""
              : "Saving will also send a notification to all enrolled learners about the new questions."
          }          
          confirmLabel="Save"
          cancelLabel="Keep Editing"
          items={questionSummaryItems}
          disabled={isSaveDisabled}
          renderTrigger={({ openModal, disabled }) => (
            <Button
              variant="contained"
              onClick={() => {
                if (disabled) {
                  return;
                }
                openModal();
              }}
              disabled={disabled}
            >
              Save All Questions
            </Button>
          )}
          onConfirm={handleSaveAll}
        />
        <ConfirmationModal
          title="Discard Changes?"
          description="You have unsaved question drafts. Do you want to discard them?"
          helperText="Discarding removes all unsaved input in this dialog."
          confirmLabel="Discard"
          cancelLabel="Keep Editing"
          disabled={false}
          renderTrigger={({ openModal }) => (
            <Button
              variant="outlined"
              color="error"
              onClick={() => {
                if (!hasUnsavedChanges) {
                  handleDialogClose();
                  return;
                }
                openModal();
              }}
            >
              Cancel
            </Button>
          )}
          onConfirm={() => {
            setStatusMessage(null);
            onClose?.();
          }}
        />
      </DialogActions>
    </Dialog>
  );
}

NewQuestionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  courseData: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    isDeleted: PropTypes.bool.isRequired,
    isDraft: PropTypes.bool.isRequired,
  }).isRequired,
  unitId: PropTypes.string.isRequired,
  level: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string,
    order: PropTypes.number,
    questions: PropTypes.arrayOf(PropTypes.object),
  }),
  startingOrder: PropTypes.number,
};

NewQuestionDialog.defaultProps = {
  onClose: undefined,
  onSave: undefined,
  level: null,
  startingOrder: 0,
};

export default NewQuestionDialog;